import React, { useEffect, useState } from 'react';
import { navigate, RouteComponentProps } from '@reach/router';
import { deleteAsset, getAsset } from 'state/assets/AssetActions';
import { Asset, AssetTypes } from 'state/assets/AssetTypes';
import isAuthenticated from 'hooks/isAuthenticated';
import { Error, ErrorCodes } from 'types';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'state/store';
import { selectSelectedAsset } from 'state/assets/AssetSelectors';
import { CLEAR_SELECTED_ASSET } from 'state/assets/AssetActionTypes';
import { RFC } from 'state/StateTypes';
import ViewContainer from 'components/ViewContainer';
import BackButton from 'components/BackButton';
import DetailHeader from 'components/DetailHeader';
import Loading from 'components/Loading';
import { convertTimeStamp, deriveDateFromMongoId, presentErrorBasedOnCode } from 'utils';
import ErrorMessage from 'components/ErrorMessage';
import styled from 'styled-components';
import { Info, InfoList } from 'components/InfoList';
import Label from 'components/Label';
import DeleteAssetConfirmationModalContent from 'components/DeleteAssetConfirmationModalContent';
import Modal from 'components/Modal';

interface Props extends RouteComponentProps {
   id?: string;
}
const AssetDetail: RFC<Props> = ({ id }) => {
   const selectedAsset: Asset | null = useSelector((state: AppState) => selectSelectedAsset(state));
   const [loading, setLoading] = useState<boolean>(true);
   const adminRoleRequired = true;
   const authenticated: boolean = isAuthenticated(adminRoleRequired);
   const [error, setError] = useState<Error | null>(null);
   const [modalOpen, setModalOpen] = useState<boolean>(false);
   const dispatch = useDispatch();

   useEffect(() => {
      (async () => {
         if (authenticated && id) {
            try {
               await dispatch(getAsset(id));
            } catch (error) {
               //if error results from missing token it will not have a json method
               error = error.json ? await error.json() : error;
               setError(error);
            }
            setLoading(false);
         }
      })();

      return () => {
         dispatch({ type: CLEAR_SELECTED_ASSET });
      };
   }, [id]);

   const deleteSelectedAsset = () => {
      setLoading(true);
      try {
         if (selectedAsset) {
            dispatch(deleteAsset(selectedAsset));
         }
      } catch (error) {
         console.log(error);
         setError(error);
      }
      setModalOpen(false);
      setLoading(false);
      navigate(selectedAsset?.type === AssetTypes.Sensation ? '/sensations' : '/waveforms');
   };
   return (
      <ViewContainer>
         <BackButton to={selectedAsset?.type === AssetTypes.Sensation ? '/sensations' : '/waveforms'} />
         {selectedAsset && (
            <DetailHeader
               title={selectedAsset?.type === AssetTypes.Sensation ? 'sensation detail' : 'waveform detail'}
               loading={loading}
               active={selectedAsset.active}
               _id={selectedAsset._id}
               onDelete={() => setModalOpen(true)}
            />
         )}
         {loading ? (
            <Loading />
         ) : error || !selectedAsset ? (
            <ErrorMessage>
               {error ? presentErrorBasedOnCode(error.code as ErrorCodes) : "Something went wrong in retrieving that waveform's details!"}
            </ErrorMessage>
         ) : (
            <DetailList>
               <Info>
                  <Label>Name</Label> {selectedAsset.name}
               </Info>
               <Info>
                  <Label>Tier</Label> {selectedAsset.tier}
               </Info>
               <Info>
                  <Label>Created</Label> {deriveDateFromMongoId(selectedAsset.createdBy)}
               </Info>
               {selectedAsset?.updatedAt && (
                  <Info>
                     <Label>Updated</Label> {selectedAsset.updatedAt ? convertTimeStamp(selectedAsset.updatedAt) : ''}
                  </Info>
               )}
               {selectedAsset?.type === AssetTypes.Sensation && (
                  <Info>
                     <Label>Description</Label> {selectedAsset.description}
                  </Info>
               )}
               <Info>
                  <Label>Bytes</Label> {selectedAsset.bytes.join(', ')}
               </Info>
            </DetailList>
         )}
         <Modal isOpen={modalOpen}>
            {selectedAsset && (
               <DeleteAssetConfirmationModalContent
                  onConfirm={deleteSelectedAsset}
                  onCancel={() => setModalOpen(false)}
                  assetType={selectedAsset.type}
                  assetName={selectedAsset.name}
               />
            )}
         </Modal>
      </ViewContainer>
   );
};

export default AssetDetail;

const DetailList = styled(InfoList)`
   margin: unset;
   padding-left: 10px;
`;
